import { useState } from 'react';
import { Kaart, KaartKop, Leeg, PaginaKop, RangBadge, Schakelaar } from '../components/ui';
import { useAuth } from '../lib/auth';
import { START_RATING, K_FACTOR, potjesVan, verwachting } from '../lib/elo';
import { toonRating } from '../lib/format';
import { DIVISIE_BREEDTE, rangFamilies, rangVan, rangVerdeling, rangVoortgang } from '../lib/prestaties';
import { useLeague } from '../lib/queries';
import { usePoule } from '../lib/poule';

/* ==========================================================================
   De rangenladder, uitgelegd.

   Eén scherm dat drie vragen beantwoordt: welke rangen zijn er, waar sta ik,
   en wat kost de volgende stap. Bereikbaar via het informatietekentje naast
   elke rang.
   ========================================================================== */

export default function Rangen() {
  const { pouleId } = usePoule();
  const { profile } = useAuth();
  const [bereik, setBereik] = useState<'poule' | 'alles'>('poule');

  const deze = useLeague(pouleId);
  const overal = useLeague(null);
  const bron = bereik === 'poule' ? deze : overal;

  const ikId = profile?.id ?? '';
  const mijnRating = bron.elo.ratings[ikId] ?? START_RATING;
  const voortgang = rangVoortgang(mijnRating);
  const speelde = potjesVan(ikId, bron.matches).length > 0;

  const verdeling = rangVerdeling(
    bron.players.filter((p) => p.isActive),
    (id) => bron.elo.ratings[id] ?? START_RATING,
    (id) => potjesVan(id, bron.matches).length,
    ikId
  );

  const families = rangFamilies();

  /* Hoeveel spelers staan er in elke familie? De verdeling rekent per divisie,
     dus tel ze hier bij elkaar op. */
  const perFamilie = families.map((f) => ({
    ...f,
    aantal: verdeling.vakken
      .filter((v) => f.divisies.some((d) => d.sleutel === v.rang.sleutel))
      .reduce((n, v) => n + v.aantal, 0),
    jij: f.divisies.some((d) => d.sleutel === rangVan(mijnRating).sleutel) && speelde
  }));

  const grootste = Math.max(1, ...perFamilie.map((f) => f.aantal));

  /* Wat levert winnen op tegen iemand van je eigen niveau? Dat is de maat
     waarin een divisie te begrijpen valt. */
  const winstGelijk = Math.round(K_FACTOR * (1 - verwachting(mijnRating, mijnRating)));

  return (
    <>
      <PaginaKop
        titel="Rangen"
        onder="Zeven rangen van elk drie divisies. Je rating bepaalt waar je staat; die begint voor iedereen op 800 en beweegt met elk bevestigd potje mee."
        rechts={
          <Schakelaar<'poule' | 'alles'>
            waarde={bereik}
            onKies={setBereik}
            opties={[
              { waarde: 'poule', label: 'Deze poule' },
              { waarde: 'alles', label: 'Alle poules' }
            ]}
          />
        }
      />

      {/* Waar sta jij, en wat kost de volgende stap. */}
      <Kaart>
        <KaartKop titel="Waar jij staat" hint={bereik === 'poule' ? 'binnen deze poule' : 'over alle poules'} />

        {speelde ? (
          <>
            <div className="flex flex-wrap items-center gap-4">
              <RangBadge rating={mijnRating} />
              <span className="kop nums text-[26px] leading-none">{toonRating(mijnRating)}</span>
              {verdeling.mijnPlek ? (
                <span className="text-sm text-krijt-600">
                  plek <span className="nums font-semibold">{verdeling.mijnPlek}</span> van {verdeling.totaal}
                </span>
              ) : null}
            </div>

            <div className="mt-4">
              <div className="flex items-baseline justify-between text-xs text-krijt-500">
                <span>{voortgang.rang.label}</span>
                <span>{voortgang.volgende ? voortgang.volgende.label : 'hoogste divisie'}</span>
              </div>
              <div className="mt-1 h-2.5 overflow-hidden rounded-full bg-krijt-200">
                <div
                  className="h-full transition-all duration-500"
                  style={{ width: `${Math.round(voortgang.deel * 100)}%`, background: voortgang.rang.kleur }}
                />
              </div>
              <p className="mt-2 text-sm text-krijt-600">
                {voortgang.volgende ? (
                  <>
                    Nog <strong className="nums">{voortgang.tekort}</strong>{' '}
                    {voortgang.tekort === 1 ? 'punt' : 'punten'} tot <strong>{voortgang.volgende.label}</strong> — zo'n{' '}
                    <strong className="nums">{Math.max(1, Math.ceil(voortgang.tekort / winstGelijk))}</strong>{' '}
                    {Math.ceil(voortgang.tekort / winstGelijk) === 1 ? 'gewonnen potje' : 'gewonnen potjes'} tegen een
                    gelijkwaardige tegenstander.
                  </>
                ) : (
                  'Je staat in de hoogste divisie. Vanaf hier valt er alleen nog te verdedigen.'
                )}
              </p>
            </div>
          </>
        ) : (
          <Leeg tekst="Speel een potje, dan verschijnt hier je rang." ikoon="bal" />
        )}
      </Kaart>

      {/* Hoe de spelers verdeeld zijn — het beeld dat je van een ladder wilt zien. */}
      <div className="mt-3.5">
        <Kaart>
          <KaartKop
            titel="Hoe iedereen verdeeld staat"
            hint={`${verdeling.totaal} ${verdeling.totaal === 1 ? 'speler' : 'spelers'} met minstens één potje`}
          />

          {verdeling.totaal ? (
            <div className="flex items-end gap-1.5 sm:gap-2.5">
              {perFamilie.map((f) => (
                <div key={f.naam} className="flex-1 text-center">
                  <div className="flex h-32 items-end justify-center">
                    <div
                      className={`w-full rounded-t-sm transition-all duration-500 ${
                        f.jij ? 'ring-2 ring-krijt-900 ring-offset-1' : ''
                      }`}
                      style={{
                        height: `${Math.max(3, (f.aantal / grootste) * 100)}%`,
                        background: f.kleur,
                        opacity: f.jij ? 1 : 0.6
                      }}
                      title={`${f.aantal} van de ${verdeling.totaal}`}
                    />
                  </div>
                  <div className="kop nums mt-1.5 text-[15px] leading-none">{f.aantal}</div>
                  <div className="opschrift truncate text-krijt-500">{f.naam}</div>
                </div>
              ))}
            </div>
          ) : (
            <Leeg tekst="Nog niemand met een gespeeld potje." ikoon="statistiek" />
          )}
        </Kaart>
      </div>

      {/* De ladder zelf, van boven naar beneden zoals je hem beklimt. */}
      <div className="mt-3.5">
        <Kaart>
          <KaartKop titel="De ladder" hint={`elke divisie is ${DIVISIE_BREEDTE} punten breed`} />

          <div className="flex flex-col gap-3">
            {families
              .slice()
              .reverse()
              .map((f) => (
                <div key={f.naam} className="flex flex-wrap items-center gap-3 border-b border-krijt-200 pb-3 last:border-b-0 last:pb-0">
                  <span className="flex w-32 shrink-0 items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="inline-block h-3 w-3 rounded-full"
                      style={{ background: f.kleur }}
                    />
                    <span className="font-semibold">{f.naam}</span>
                  </span>

                  {/* Binnen een familie van links naar rechts oplopend, zodat de
                      ratings in leesrichting stijgen: IJzer I, dan II, dan III. */}
                  <span className="flex flex-wrap gap-1.5">
                    {f.divisies.map((d) => {
                      const hier = speelde && rangVan(mijnRating).sleutel === d.sleutel;
                      return (
                        <span
                          key={d.sleutel}
                          className={`nums rounded-sm border px-2 py-1 text-xs ${
                            hier ? 'border-krijt-900 bg-krijt-900 font-semibold text-white' : 'border-krijt-200 bg-krijt-50'
                          }`}
                        >
                          {d.divisie} · {d.vanaf === 0 ? 'tot' : 'vanaf'}{' '}
                          {d.vanaf === 0 ? d.tot : d.vanaf}
                          {hier ? ' · jij' : ''}
                        </span>
                      );
                    })}
                  </span>
                </div>
              ))}
          </div>
        </Kaart>
      </div>

      {/* De regels erachter, in gewone taal. */}
      <div className="mt-3.5 grid gap-3.5 lg:grid-cols-2">
        <Kaart>
          <KaartKop titel="Hoe je stijgt en zakt" />
          <dl className="space-y-3 text-sm leading-relaxed text-krijt-600">
            <div>
              <dt className="font-semibold text-krijt-900">Iedereen begint op {START_RATING}</dt>
              <dd>
                Dat is precies het midden van <strong>Zilver II</strong>. Onder je liggen zes divisies om in te zakken,
                boven je dertien om te beklimmen.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-krijt-900">Wie je verslaat telt mee</dt>
              <dd>
                Winnen van iemand die hoger staat levert meer op dan winnen van iemand die lager staat — en verliezen
                van iemand die lager staat kost meer. Tegen een gelijkwaardige tegenstander gaat het om ongeveer{' '}
                <strong className="nums">{winstGelijk}</strong> punten.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-krijt-900">Een divisie is {DIVISIE_BREEDTE} punten</dt>
              <dd>
                Eén potje verschuift je dus hooguit een derde divisie. Genoeg om beweging te zien, te weinig om in één
                middag van Brons naar Goud te springen.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-krijt-900">Alleen bevestigde potjes tellen</dt>
              <dd>
                Zolang de andere kant de stand niet erkent, staat je rang stil. Na vier dagen telt een uitslag vanzelf.
              </dd>
            </div>
          </dl>
        </Kaart>

        <Kaart>
          <KaartKop titel="Rangen en tiers zijn niet hetzelfde" />
          <p className="text-sm leading-relaxed text-krijt-600">
            Verwarrend genoeg heten ze bijna gelijk, dus het verschil op een rij:
          </p>
          <dl className="mt-3 space-y-3 text-sm leading-relaxed text-krijt-600">
            <div>
              <dt className="font-semibold text-krijt-900">Een rang zegt hoe goed je bent</dt>
              <dd>Die volgt uit je rating en kan omhoog én omlaag. Dat is deze pagina.</dd>
            </div>
            <div>
              <dt className="font-semibold text-krijt-900">Een tier zegt hoe vaak je iets deed</dt>
              <dd>
                Die hoort bij een prestatie — tien afgangen, honderd potjes — en zakt nooit. Je vindt ze op je profiel.
              </dd>
            </div>
            <div>
              <dt className="font-semibold text-krijt-900">Je niveau zegt hoe lang je meedoet</dt>
              <dd>Dat telt punten op uit alles wat je speelt en wint, en zakt ook nooit.</dd>
            </div>
          </dl>
        </Kaart>
      </div>
    </>
  );
}
